import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'
import {diffWordsWithSpace} from 'diff'

import {FC, useState, useEffect} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Card, Row, Col, Form, Button, Spinner, InputGroup} from 'react-bootstrap'
import {
  getChapterReviewById,
  getParentChapters,
  postChapterReview,
  verifySourceUrl,
} from './core/_requests'

import useAxiosPrivate from '../../../libs/useAxiosPrivate'
import {useNavigate, useParams} from 'react-router-dom'
import './TradeDataAgreementChapterReviewNewPage.scss'

import SummernoteEditor from '../../../../ui-kit/SummerNote/SummerNote'
import {AgreementChapterNew} from '../trade-data-agreement-chapter-new/core/_models'

const ReactSwal = withReactContent(Swal)
const confirmDialog = (title: string) => {
  return ReactSwal.fire({
    title,
    icon: 'question',
    showCloseButton: true,
    showCancelButton: true,
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    customClass: {
      title: 'fw-bold',
      cancelButton: 'btn btn-danger btn-sm',
      confirmButton: 'btn btn-success btn-sm',
    },
    padding: '1rem',
  })
}

const createHighlightedDiff = (oldText: string, newText: string) => {
  const diff = diffWordsWithSpace(oldText || '', newText || '')

  const html = diff
    .map((part) => {
      if (part.added) return `<span style="background-color:#d4edda;">${part.value}</span>`
      if (part.removed)
        return `<span style="background-color:#f8d7da;text-decoration:line-through;">${part.value}</span>`
      return part.value
    })
    .join('')

  return html
}

const TradeDataAgreementChapterReviewNewPage: FC = () => {
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const {idReview} = useParams()

  const [isLoadingAgreement, setIsLoadingAgreement] = useState(false)
  const [isLoadingButton, setIsLoadingButton] = useState(false)
  const [isFormValid, setIsFormValid] = useState(false)

  const [wordCount, setWordCount] = useState(0)
  const [agreementDetail, setAgreementDetail] = useState({
    agreement: '',
  })
  const [parentChapters, setParentChapters] = useState<AgreementChapterNew[]>([])
  const [agreementChapterOrigin, setAgreementChapterOrigin] = useState<AgreementChapterNew>({
    id: '',
    title: '',
    label: '',
    type: '',
    nwords_new: null,
    parent_id: null,
    clean_content: null,
    sort_order: null,
    source: undefined,
  })
  const [agreementChapterPending, setAgreementChapterPending] = useState<AgreementChapterNew>({
    id: '',
    title: '',
    label: '',
    type: '',
    nwords_new: null,
    parent_id: null,
    clean_content: null,
    sort_order: null,
    rta_content_id: null,
    source: undefined,
  })
  const [isSourceLinkVerified, setIsSourceLinkVerified] = useState<boolean | null>(null)
  const [isLoadingVerify, setIsLoadingVerify] = useState(false)

  const handleSourceLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsSourceLinkVerified(null)
    const {value} = e.target
    setAgreementChapterPending((prev) => ({
      ...prev,
      source: value,
      is_source_valid: null,
    }))
  }

  const handleVerifySourceUrl = async (url: string) => {
    setIsLoadingVerify(true)
    try {
      const response = await verifySourceUrl(axiosPrivate, url)
      const isValid = response.data.isVerified === true
      setIsSourceLinkVerified(isValid)
      setAgreementChapterPending((prev) => ({
        ...prev,
        is_source_valid: isValid,
      }))
    } catch (error) {
      setIsSourceLinkVerified(false)
      setAgreementChapterPending((prev) => ({...prev, is_source_valid: false}))
    } finally {
      setIsLoadingVerify(false)
    }
  }

  const convertFromHTML = (html: string): string => {
    const text = html
      .replace(/<[^>]+>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
    return text
  }

  const countWordsFromHtml = (html: string): number => {
    const text = convertFromHTML(html)
    if (!text) return 0
    return text.split(' ').length
  }

  const [highlightedDiffDisplay, setHighlightedDiffDisplay] = useState('')

  const fetchOptionAgreement = async () => {
    try {
      setIsLoadingAgreement(true)

      const detailRes = await getChapterReviewById(axiosPrivate, {idReview})

      if (detailRes?.data) {
        const {agreement, chapter_pending_review, chapter_origin} = detailRes.data
        const parentChaptersRes = await getParentChapters(axiosPrivate, {
          idAgreement: detailRes?.data.chapter_pending_review?.rta_id,
        })

        setAgreementChapterPending(chapter_pending_review)
        setIsSourceLinkVerified(chapter_pending_review.is_source_valid)
        setAgreementDetail({agreement})
        setAgreementChapterPending(chapter_pending_review)
        setWordCount(chapter_pending_review?.nwords_new)
        setParentChapters(parentChaptersRes?.data?.data || [])

        if (chapter_origin) {
          setAgreementChapterOrigin(chapter_origin)

          const oldDisplay = chapter_origin?.display_content ?? ''
          const newDisplay = chapter_pending_review?.display_content ?? ''

          const diffDisplayHTML = createHighlightedDiff(oldDisplay, newDisplay)
          setHighlightedDiffDisplay(diffDisplayHTML)
        }
      }
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoadingAgreement(false)
    }
  }

  const isDifferent = (originVal: any, newVal: any) => {
    if (!originVal && !newVal) return false
    return String(originVal || '').trim() !== String(newVal || '').trim()
  }

  useEffect(() => {
    fetchOptionAgreement()
  }, [])

  // Add or Edit
  const handleValueChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const {name, value} = e.target
    setAgreementChapterPending((prev) => ({
      ...prev,
      [name]: value,
    }))
  }

  const validateRequiredFields = () => {
    const requiredFields = ['title', 'label', 'type']

    const isValid = requiredFields.every((key) => {
      const val = (agreementChapterPending as any)[key]
      return val !== undefined && val !== null && String(val).trim() !== ''
    })

    setIsFormValid(isValid)
  }

  useEffect(() => {
    validateRequiredFields()
  }, [agreementChapterPending])

  const handleSubmit = () => {
    const body = {
      type: agreementChapterPending.type,
      label: agreementChapterPending.label,
      title: agreementChapterPending.title,
      rta_id: agreementChapterPending.rta_id,
      clean_content: agreementChapterPending.clean_content,
      nwords_new: agreementChapterPending.nwords_new,
      display_content: agreementChapterPending.display_content ?? '',
      parent_id: agreementChapterPending.parent_id,
      sort_order: agreementChapterPending.sort_order,
      rta_content_id: agreementChapterPending.rta_content_id,
      source: agreementChapterPending.source || '',
      is_source_valid: agreementChapterPending.source?.trim()
        ? isSourceLinkVerified ?? false
        : null,
    }

    confirmDialog(`Are you sure you want to submit this chapter?`).then((result) => {
      if (!result.isConfirmed) return // exit if user cancels

      setIsLoadingButton(true)

      postChapterReview(axiosPrivate, agreementChapterPending.id, body)
        .then((res) => {
          if (res.status === 200) {
            ReactSwal.fire({
              title: 'Data reviewed',
              icon: 'success',
              confirmButtonText: 'OK',
            }).then(() => {
              navigate(`/new-rta/${agreementChapterPending.rta_id}`)
            })
          }
        })
        .catch((err) => {
          console.error(err)
          ReactSwal.fire('Error', 'Failed to review data', 'error')
        })
        .finally(() => {
          setIsLoadingButton(false)
        })
    })
  }

  const handleCancel = () => {
    confirmDialog('Are you sure you want to cancel?').then((result) => {
      if (result.isConfirmed) {
        navigate(`/new-rta/${agreementChapterPending.rta_id}`)
      }
    })
  }
  // End of Add or Edit

  return (
    <>
      <PageTitle
        breadcrumbs={[
          {
            title: 'Home',
            path: '/',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: 'Trade Data Agreement',
            path: 'new-rta',
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
          {
            title: agreementDetail.agreement ?? '-',
            path: `new-rta/${agreementChapterPending.rta_id}`,
            isSeparator: false,
            isActive: false,
          },
          {
            title: '',
            path: '',
            isSeparator: true,
            isActive: false,
          },
        ]}
      >
        Review Chapter
      </PageTitle>
      <div className='TradeDataAgreementChapterReviewPage'>
        <Row className='mb-5'>
          <Col>
            <Card className='mb-5'>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-file-alt px-2'></span>
                      <h3 className='text-gray-700'>Agreement Information</h3>
                    </div>
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {!isLoadingAgreement ? (
                  <div className='row'>
                    <div className='col-md-12 mb-3'>
                      <Form.Label className='text-primary me-0 ps-0' column>
                        Agreement Name
                      </Form.Label>
                      <div className='data-value'>{agreementDetail.agreement}</div>
                    </div>
                  </div>
                ) : (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{height: '50vh'}}
                  >
                    <Spinner animation='border' role='status' variant='primary' className='me-3' />
                    <div className='loading-text'>
                      Loading
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
            <Card>
              <Card.Header className='card-header-stretch'>
                <Card.Title className='w-100'>
                  <div className='title-container w-100 d-flex justify-content-between align-items-center'>
                    <div className='title d-flex align-items-center'>
                      <span className='fa fa-file-alt px-2'></span>
                      <h3 className='text-gray-700'>Review Chapter</h3>
                    </div>
                  </div>
                </Card.Title>
              </Card.Header>
              <Card.Body>
                {!isLoadingAgreement ? (
                  <Form className='form-agreement'>
                    <div className='row'>
                      <div className='chapter-review'>
                        {agreementChapterOrigin.id !== '' && (
                          <div className='chapter-information chapter-information-before'>
                            <div className='row mb-5'>
                              <div className='col-lg-12'>
                                <h2 className='information-title'>Origin Content</h2>
                              </div>
                            </div>
                            <div className='row'>
                              <div className='col-12'>
                                <h3>Content Information</h3>
                              </div>
                              <div className='col-12'>
                                <Form.Label className='text-primary me-0 ps-0' column>
                                  Content Type
                                </Form.Label>
                                <Form.Control
                                  type='text'
                                  placeholder={'Content Type'}
                                  value={
                                    agreementChapterOrigin.type
                                      ? String(agreementChapterOrigin.type)
                                          .charAt(0)
                                          .toUpperCase() +
                                        String(agreementChapterOrigin.type).slice(1)
                                      : ''
                                  }
                                  readOnly
                                />
                              </div>
                              <div className='col-md-6 mb-3'>
                                <Form.Label className='text-primary me-0 ps-0' column>
                                  Content Name
                                </Form.Label>
                                <Form.Control
                                  type='text'
                                  placeholder={'Content Label'}
                                  value={agreementChapterOrigin.label ?? ''}
                                  readOnly
                                />
                              </div>
                              <div className='col-12 col-md-6'>
                                <Form.Label className='text-primary me-0 ps-0' column>
                                  Content Title
                                </Form.Label>
                                <Form.Control
                                  type='text'
                                  placeholder={'Content Title'}
                                  value={agreementChapterOrigin.title ?? ''}
                                  readOnly
                                />
                              </div>
                              <div className='col-12 col-md-6'>
                                <Form.Label className='text-primary me-0 ps-0' column>
                                  Content Parent
                                </Form.Label>
                                <Form.Control
                                  type='text'
                                  placeholder={'Content Parent'}
                                  value={
                                    parentChapters.find(
                                      (p) => p.id === agreementChapterOrigin.parent_id
                                    )?.title ?? ''
                                  }
                                  readOnly
                                />
                              </div>
                              <div className='col-12 col-md-6'>
                                <Form.Label className='text-primary me-0 ps-0' column>
                                  Content Sort Order
                                </Form.Label>
                                <Form.Control
                                  type='text'
                                  placeholder={'Content Sort Order'}
                                  value={agreementChapterOrigin.sort_order ?? ''}
                                  readOnly
                                />
                              </div>
                            </div>
                            <hr />
                            <div className='row'>
                              <div className='col-12'>
                                <h3>Source</h3>
                              </div>
                              <div className='col-md-12 mb-3'>
                                <Form.Label className='text-primary me-0 ps-0' column>
                                  Link
                                </Form.Label>
                                <Form.Control
                                  type='text'
                                  name='source'
                                  placeholder='Enter link source'
                                  value={agreementChapterOrigin.source ?? ''}
                                  disabled={true}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                        <div className='chapter-information chapter-information-after'>
                          <div className='row mb-5'>
                            <div className='col-lg-12'>
                              <h2 className='information-title'>Pending Review Content</h2>
                            </div>
                          </div>
                          <div className='row'>
                            <div className='col-12'>
                              <h3>Content Information</h3>
                            </div>
                            <div className='col-12'>
                              <Form.Label className='text-primary me-0 ps-0' column>
                                <span
                                  className={`required ${
                                    isDifferent(
                                      agreementChapterOrigin.type,
                                      agreementChapterPending.type
                                    )
                                      ? 'label-highlight'
                                      : ''
                                  }`}
                                >
                                  Content Type
                                </span>
                              </Form.Label>
                              <Form.Select
                                name='type'
                                value={agreementChapterPending.type ?? ''}
                                onChange={handleValueChange}
                              >
                                <option value='' selected={agreementChapterPending.type === ''}>
                                  Select Content Type
                                </option>
                                <option
                                  value='chapter'
                                  selected={agreementChapterPending.type === 'chapter'}
                                >
                                  Chapter
                                </option>
                                <option
                                  value='part'
                                  selected={agreementChapterPending.type === 'part'}
                                >
                                  Part
                                </option>
                                <option
                                  value='section'
                                  selected={agreementChapterPending.type === 'section'}
                                >
                                  Section
                                </option>
                                <option
                                  value='article'
                                  selected={agreementChapterPending.type === 'article'}
                                >
                                  Article
                                </option>
                                <option
                                  value='subsection'
                                  selected={agreementChapterPending.type === 'subsection'}
                                >
                                  Subsection
                                </option>
                              </Form.Select>
                            </div>
                            <div className='col-12 col-md-6 mb-3'>
                              <Form.Label className='text-primary me-0 ps-0' column>
                                <span
                                  className={`required ${
                                    isDifferent(
                                      agreementChapterOrigin.label,
                                      agreementChapterPending.label
                                    )
                                      ? 'label-highlight'
                                      : ''
                                  }`}
                                >
                                  Content Name
                                </span>
                              </Form.Label>
                              <Form.Control
                                type='text'
                                name='article_name'
                                placeholder={'Content Label'}
                                value={agreementChapterPending.label ?? ''}
                                onChange={handleValueChange}
                              />
                            </div>
                            <div className='col-12 col-md-6'>
                              <Form.Label className='text-primary me-0 ps-0' column>
                                <span
                                  className={`required ${
                                    isDifferent(
                                      agreementChapterOrigin.title,
                                      agreementChapterPending.title
                                    )
                                      ? 'label-highlight'
                                      : ''
                                  }`}
                                >
                                  Content Title
                                </span>
                              </Form.Label>
                              <Form.Control
                                type='text'
                                name='title'
                                placeholder={'Content Title'}
                                value={agreementChapterPending.title ?? ''}
                                onChange={handleValueChange}
                              />
                            </div>
                            <div className='col-12 col-md-6'>
                              <Form.Label className='text-primary me-0 ps-0' column>
                                <span
                                  className={`${
                                    isDifferent(
                                      agreementChapterOrigin.parent_id,
                                      agreementChapterPending.parent_id
                                    )
                                      ? 'label-highlight'
                                      : ''
                                  }`}
                                >
                                  Content Parent
                                </span>
                              </Form.Label>
                              <Form.Select
                                name='parent_id'
                                value={agreementChapterPending.parent_id ?? ''}
                                onChange={handleValueChange}
                              >
                                <option value=''>Select Content Parent</option>
                                {parentChapters.map((value, idx) => (
                                  <option
                                    key={idx}
                                    value={value.id}
                                    selected={agreementChapterPending.parent_id === value.id}
                                  >
                                    {value.title}&nbsp;({value.label})
                                  </option>
                                ))}
                              </Form.Select>
                            </div>
                            <div className='col-12 col-md-6'>
                              <Form.Label className='text-primary me-0 ps-0' column>
                                <span
                                  className={`${
                                    isDifferent(
                                      agreementChapterOrigin.sort_order,
                                      agreementChapterPending.sort_order
                                    )
                                      ? 'label-highlight'
                                      : ''
                                  }`}
                                >
                                  Content Sort Order
                                </span>
                              </Form.Label>
                              <Form.Control
                                type='text'
                                name='sort_order'
                                placeholder={'Content Sort Order'}
                                value={agreementChapterPending.sort_order ?? ''}
                                readOnly={true}
                              />
                            </div>
                            <div className='col-12 col-md-6'></div>
                          </div>
                          <hr />
                          <div className='row'>
                            <div className='col-12'>
                              <h3>Source</h3>
                            </div>
                            <div className='col-md-12 mb-3'>
                              <Form.Label className='text-primary me-0 ps-0' column>
                                <span
                                  className={
                                    isDifferent(
                                      agreementChapterOrigin.source,
                                      agreementChapterPending.source
                                    )
                                      ? 'label-highlight'
                                      : ''
                                  }
                                >
                                  Link
                                </span>
                              </Form.Label>
                              {isSourceLinkVerified !== null && (
                                <div className='mb-2'>
                                  <Form.Text
                                    className={
                                      isSourceLinkVerified ? 'text-success' : 'text-danger'
                                    }
                                  >
                                    {isSourceLinkVerified ? 'Valid URL' : 'Invalid URL'}
                                  </Form.Text>
                                </div>
                              )}
                              <InputGroup hasValidation>
                                <Form.Control
                                  type='text'
                                  name='source'
                                  isInvalid={
                                    !!agreementChapterPending.source &&
                                    !/^https?:\/\//.test(agreementChapterPending.source)
                                  }
                                  placeholder='Enter link source'
                                  value={agreementChapterPending.source ?? ''}
                                  onChange={handleSourceLinkChange}
                                />
                                <Button
                                  variant='outline-secondary'
                                  disabled={
                                    !agreementChapterPending.source?.trim() ||
                                    !/^https?:\/\//.test(agreementChapterPending.source) ||
                                    isLoadingVerify
                                  }
                                  onClick={() =>
                                    handleVerifySourceUrl(agreementChapterPending.source!)
                                  }
                                >
                                  {isLoadingVerify && (
                                    <Spinner animation='border' size='sm' className='me-2' />
                                  )}
                                  Verify
                                </Button>
                                <Form.Control.Feedback type='invalid'>
                                  URL must start with http:// or https://
                                </Form.Control.Feedback>
                              </InputGroup>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <hr />
                    <div className='row'>
                      <div className='col-md-12'>
                        <Form.Label className='text-primary me-0 ps-0 label-with-info' column>
                          Content Text Display
                          <div className='word-count'>
                            {agreementChapterOrigin.nwords_new !== wordCount && (
                              <span className='diff-word-count'>
                                {agreementChapterOrigin.nwords_new}
                              </span>
                            )}
                            <span>{wordCount}</span>
                          </div>
                        </Form.Label>
                        {agreementChapterPending.rta_content_id &&
                          agreementChapterOrigin.display_content !==
                            agreementChapterPending.display_content && (
                            <div
                              className='mb-3 p-3 border rounded bg-light'
                              dangerouslySetInnerHTML={{__html: highlightedDiffDisplay}}
                            />
                          )}
                        <SummernoteEditor
                          value={agreementChapterPending.display_content || ''}
                          onChange={(html) => {
                            setAgreementChapterPending((prev) => ({
                              ...prev,
                              display_content: html,
                            }))
                            setWordCount(countWordsFromHtml(html))
                          }}
                        />
                      </div>
                      <div className='row pt-5'>
                        <div className='col-lg-12 d-flex justify-content-end gap-3'>
                          <Button variant='default' className='btn-cancel' onClick={handleCancel}>
                            Cancel
                          </Button>
                          <Button
                            variant='primary'
                            onClick={handleSubmit}
                            disabled={
                              isLoadingButton ||
                              !isFormValid ||
                              (agreementChapterPending.source!.trim()!.length > 0 &&
                                agreementChapterPending.is_source_valid === null)
                            }
                          >
                            {isLoadingButton ? (
                              <>
                                <Spinner animation='border' size='sm' className='me-2' />
                                Submit
                              </>
                            ) : (
                              'Submit'
                            )}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </Form>
                ) : (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{height: '50vh'}}
                  >
                    <Spinner animation='border' role='status' variant='primary' className='me-3' />
                    <div className='loading-text'>
                      Loading
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                      <span className='dot'>.</span>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </>
  )
}

export default TradeDataAgreementChapterReviewNewPage
